import {
    Form, FormButton, FormButtonContainer, FormItem, useForm, useAlerts, TextInput
} from "@sis/react-library"
import { Example2Dto } from "./FormSamplesNonFieldtemsPage";
import { useState } from "react";
import { ProfilePictureFormCoverImage } from "./CustomComponents/ProfilePictureFormCoverImage";

/////////////////////////////////////////////////////////////////////////////////////////////
// In this example, we want to submit a profile picture for the user.
// The picture will be submitted seperately, because the type is of File which cannot be
// part of a json structure            
// However we still want the form to handle field input, like validation 
// (ie we can't submit the form if no picture is selected,
// and to throw an error to say please select a picture.)
// and to enable the form to submit if only a picture is changed. (mark the form as non pristine).<br />
//
// We will create a new excludeOnSubmit field which will handle if the profile picture has changed.
// This will be passed through in additionalItems to tell the form about the field.
// The form field is then passed through to a non form component which will handle the file
// input.
/////////////////////////////////////////////////////////////////////////////////////////////

/////////////////////////////////////////////////////////////////////////////////////////////
// Interface for the form (see basic usage example for more information).
// We are defining a field only used in this form (which will handle the profile changes)
/////////////////////////////////////////////////////////////////////////////////////////////
interface T extends Example2Dto {
    profilePictureFileChanged?: boolean;
}

interface FormSamplesNonFieldtemsExample2FormProps {
    onSubmit: (values: Example2Dto, profilePictureFile: File | undefined) => Promise<boolean | undefined>;
    submitting: boolean;
}

export const FormSamplesNonFieldtemsExample2Form = ({
    onSubmit, submitting
}: FormSamplesNonFieldtemsExample2FormProps) => {

    const formName = 'FormNonFieldtemsExample2';

    const [profilePictureFile, setProfilePictureFile] = useState<File | undefined>(undefined);

    const { resetForm, setItem, getItem } = useForm<T>(formName);
    const { confirm } = useAlerts();

    const handleSubmit = async (values: T) => {
        return await onSubmit(values, profilePictureFile);
    }

    const handleCancel = () => {
        confirm('Cancel', 'Are you sure you want to cancel and loose your changes?',
            () => { resetForm() },
            "Yes", "No"
        );
    }

    const handleProfilePictureChange = (newFile: File) => {
        setProfilePictureFile(newFile);
        setItem("profilePictureFileChanged", true);
    }

    return (
        <Form<T>
            name={formName}
            onSubmit={handleSubmit}
            submitting={submitting}
            beforeLeaveText='You have made changes, are you sure you want to leave ?'
            ////////////////////////////////////////////////////////////////////////////////////////////////////////////
            // additionalItems is where you can create extra items for the form to keep
            // track of where there's no user input.
            // Here we are keeping track if the id as well as if the profile picture has been changed
            // Note: the Id won't appear in the submitValues as it will be undefined as no initial value is set for it
            ////////////////////////////////////////////////////////////////////////////////////////////////////////////
            additionalItems={[
                { name: "id" },
                {
                    name: "profilePictureFileChanged", initialValue: undefined, excludeOnSubmit: true,
                    rules: [{ type: "required", message: "Please upload a photo!" }]
                }
            ]}
        >
            <ProfilePictureFormCoverImage
                profilePictureFile={profilePictureFile}
                onProfilePictureChange={handleProfilePictureChange}
                submitting={submitting}
                formItemProperties={getItem("profilePictureFileChanged")}
            />
            <FormItem<T>
                formField="firstName"
                label="First Name"
                rules={[{ type: "required", message: "Please enter a firstName!" }]}
                element={<TextInput maxLength={20} />}
            />
            <FormItem<T>
                formField="lastName"
                label="Last Name"
                rules={[{ type: "required", message: "Please enter a lastName!" }]}
                element={<TextInput maxLength={20} />}
            />
            <FormButtonContainer>
                <FormButton
                    formButtonText='Cancel'
                    onClick={() => { handleCancel() }}
                />
                <FormButton
                    formButtonText='Submit'
                    submitButton
                />
            </FormButtonContainer>
        </Form>
    )
}